import { SBTranslate } from "i18n";
import { IPurchasePlan } from "modules/Purchases/models/IPurchasePlans";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SBSummarization from "views/Commons/SBSummarization";

export const PurchasesSummarization = ({
	items,
	selectedItems,
	withShadow,
	summaryBase,
}: {
	items: IPurchasePlan[];
	selectedItems?: IPurchasePlan[];
	withShadow: boolean;
	summaryBase?: any;
}) => {
	const { t, i18n } = useTranslation();

	const sumOfPurchaseUnits = (plans: IPurchasePlan[], useSummaryBase: boolean = false) => {
		const localModifierSum = plans.reduce((a: number, b: IPurchasePlan) => a + +(b.modifier || 0), 0);
		let baseSum = 0;
		if (useSummaryBase && summaryBase) {
			baseSum = summaryBase.totalPurchaseUnits || 0;
		} else {
			baseSum = plans.reduce((a: number, b: IPurchasePlan) => a + +b.quantity, 0);
		}
		return (baseSum + localModifierSum).toLocaleString("pt-BR", { minimumFractionDigits: 0 });
	};

	const sumOfEstimatedCostPlusFreight = (plans: IPurchasePlan[], useSummaryBase: boolean = false) => {
		const localModifierCostSum = plans.reduce(
			(a: number, b: IPurchasePlan) => a + (+(b.modifier || 0) * (b.unitCost || 0)),
			0
		);
		let baseCostSum = 0;
		if (useSummaryBase && summaryBase) {
			baseCostSum = summaryBase.totalEstimatedCost || 0;
		} else {
			baseCostSum = plans.reduce((a: number, b: IPurchasePlan) => a + ((b.quantity || 0) * (b.unitCost || 0)), 0);
		}
		return (baseCostSum + localModifierCostSum).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
	};

	const countOfInventories = (plans: IPurchasePlan[], useSummaryBase: boolean = false) => {
		if (useSummaryBase && summaryBase) return summaryBase.totalInventories || 0;
		return plans.map((c) => c.inventory.id).distinct().length;
	};

	const countOfAlerts = (plans: IPurchasePlan[], useSummaryBase: boolean = false) => {
		if (useSummaryBase && summaryBase) return summaryBase.totalAlerts || 0;
		return plans.filter((plan) => plan.warning.indexOf("None") === -1).length;
	};

	const countOfEmergencials = (plans: IPurchasePlan[], useSummaryBase: boolean = false) => {
		if (useSummaryBase && summaryBase) return summaryBase.totalEmergencials || 0;
		return plans.filter((plan) => plan.cycle?.isEmergencial).length;
	};

	const summarizations = useMemo(
		() => [
			{
				name: SBTranslate("purchaseUnit"),
				tip: SBTranslate("tips.purchaseUnit"),
				icon: "package",
				color: "darkPurple",
				value: sumOfPurchaseUnits(items, true),
				selectedItems:
					!!selectedItems && selectedItems.length > 0
						? sumOfPurchaseUnits(selectedItems, false)
						: undefined,
			},
			{
				name: SBTranslate("estimatedCostPlusFreight"),
				tip: SBTranslate("tips.estimatedCostPlusFreight"),
				icon: "dollar-sign",
				color: "darkPurple",
				value: sumOfEstimatedCostPlusFreight(items, true),
				selectedItems:
					!!selectedItems && selectedItems.length > 0
						? sumOfEstimatedCostPlusFreight(selectedItems, false)
						: undefined,
			},
			{
				name: SBTranslate("inventory_other"),
				tip: SBTranslate("tips.inventoryRecomendation"),
				icon: "package",
				color: "darkPurple",
				value: countOfInventories(items, true),
				selectedItems:
					!!selectedItems && selectedItems.length > 0
						? countOfInventories(selectedItems, false)
						: undefined,
			},
			{
				name: SBTranslate("alert_other"),
				tip: SBTranslate("tips.alertRecomendation"),
				icon: "sirene",
				fillColor: "darkPurple",
				value: countOfAlerts(items, true),
				selectedItems:
					!!selectedItems && selectedItems.length > 0
						? countOfAlerts(selectedItems, false)
						: undefined,
			},
			{
				name: SBTranslate("isEmergencial"),
				tip: SBTranslate("tips.isEmergencial"),
				icon: "alert-triangle",
				color: "darkPurple",
				value: countOfEmergencials(items, true),
				selectedItems:
					!!selectedItems && selectedItems.length > 0
						? countOfEmergencials(selectedItems, false)
						: undefined,
			},
		],
		[items, selectedItems, i18n.language, summaryBase]
	);

	if ((!items || items.length === 0) && (!summaryBase || summaryBase.totalCount === 0)) return null;

	return <SBSummarization items={summarizations} withShadow={withShadow} />;
};
